import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const iltmCannes: ChauffeurPage = {
  slug: "iltm-cannes",
  title: "Private chauffeur ILTM Cannes – luxury B2B VTC",
  subtitle:
    "Private chauffeur during ILTM in Cannes: executive transfers, appointment-dense schedules at the Palais and low-season Nice airport runs.",
  keywords: [
    "iltm cannes chauffeur",
    "iltm cannes private chauffeur",
    "luxury travel market chauffeur cannes",
  ],
  hero: {
    title: "Private chauffeur during ILTM Cannes",
    subtitle:
      "In early December, the global luxury-travel marketplace gathers invitation-only buyers and sellers at the Palais: a chauffeur for tightly timed appointment days.",
    keywords: [
      "ILTM Cannes chauffeur",
      "luxury travel VTC Cannes",
      "Palais des Festivals chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during ILTM in Cannes",
    badges: [
      "Executive transfers",
      "Appointment schedule",
      "Low-season airport",
    ],
  },
  benefits: {
    heading: "Why a chauffeur during ILTM",
    items: [
      {
        title: "Timed appointments",
        description:
          "ILTM runs on pre-scheduled appointments of a few minutes each: absolute punctuality is required, and a waiting chauffeur smooths every transition.",
      },
      {
        title: "Invitation-only show",
        description:
          "UHNW buyers and high-end travel advisors: uniformed service, discretion and standing to match the clientele.",
      },
      {
        title: "Low-season airport",
        description:
          "Early December is off the tourist peak but the weather varies: flight tracking and welcome for calm arrivals at any hour.",
      },
      {
        title: "Premium hospitality",
        description:
          "Networking dinners, brand suites and partner evenings: links and late-night returns secured throughout.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during ILTM Cannes",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice ↔ Cannes link with flight tracking for buyers, exhibitors and travel advisors, even on December's off-peak schedules.",
      },
      {
        title: "Executive hourly hire",
        text: "A dedicated chauffeur by the half-day or day to keep a tight appointment schedule at the Palais.",
      },
      {
        title: "Multi-stop circuits",
        text: "Optimised routes between the Palais, the hotels and the partners' hospitality suites.",
      },
      {
        title: "Delegation shuttles",
        text: "Multi-vehicle coordination for buyer groups, hotel brands and tourism boards.",
      },
      {
        title: "Networking dinners & evenings",
        text: "Receptions, partner dinners and business evenings, with a guaranteed late-night return.",
      },
    ],
  },
  locations: {
    heading: "Key points during ILTM",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "The show's appointment areas, stands and luxury brand suites.",
      },
      {
        name: "La Croisette & palaces",
        description:
          "Carlton, Martinez, Majestic: lodging for delegations and networking dinners.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, international arrivals in the early-December low season.",
      },
      {
        name: "Mougins & hinterland",
        description: "Gourmet tables and partner dinners outside the centre.",
      },
      {
        name: "Antibes / Juan-les-Pins",
        description: "Additional hotels and team logistics.",
      },
    ],
  },
  generic: {
    heading: "ILTM from a chauffeur's seat",
    subheading:
      "In early December, the global luxury-travel trade gathers at the Palais by invitation — a B2B show where every appointment is timed.",
    paragraphs: [
      "Every early December, the International Luxury Travel Market brings high-end travel buyers and sellers to Cannes, by invitation. The show is built on pre-scheduled, timed appointments at the Palais des Festivals: punctuality is a requirement, and a chauffeur waiting between meeting blocks avoids any delay on a dense agenda.",
      "Delegations arrive via Nice Côte d'Azur airport. We're outside the summer tourist peak, but December weather can vary: flight tracking and welcome guarantee calm arrivals, including on off-peak schedules.",
      "ILTM extends into hospitality: brand suites, networking dinners and partner evenings punctuate the Croisette and the hinterland. Multi-stop circuits and hourly hire keep the same chauffeur, with late-night returns after the dinners.",
    ],
    bullets: [
      "Timed appointments respected",
      "Flight tracking in early December",
      "Premium hospitality",
    ],
  },
  process: {
    heading: "Booking a chauffeur for ILTM Cannes",
    steps: [
      "Tell us your dates, your hotel and your appointment schedule at the Palais.",
      "We build the multi-stop circuits and airport links around your timed agenda.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Daily and evening hire for appointments, brand suites and dinners.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur during ILTM Cannes?",
      a: "Book ahead: in early December, ILTM concentrates premium demand on Cannes. Share your dates, your hotel and your appointment schedule so we can secure a dedicated chauffeur for the whole show.",
    },
    {
      q: "Can you keep a very tight appointment schedule?",
      a: "Yes. ILTM runs on pre-scheduled, timed appointments: we build optimised circuits and keep a chauffeur waiting between your meeting blocks to absorb any slip and preserve punctuality.",
    },
    {
      q: "Are airport transfers available in low season?",
      a: "Yes, with flight tracking and welcome. Early December is outside the summer peak, but the weather can vary: we adjust the pick-up time and cover international arrivals at any hour.",
    },
    {
      q: "Do you cover networking dinners and evenings?",
      a: "Yes. Brand suites, partner dinners and networking evenings run on into the night on the Croisette or in the hinterland: the chauffeur waits and guarantees your late-night return.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a Cannes ⇄ Nice Airport transfer",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Book a Cannes ⇄ Monaco transfer",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Check availability",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for ILTM Cannes",
    text: "Executive transfers, a timed appointment schedule and premium hospitality: book a dedicated chauffeur for the show.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "ILTM Cannes — luxury B2B car service, Palais des Festivals",
    description:
      "Private chauffeur during ILTM in Cannes: executive transfers, appointment-dense schedules at the Palais, low-season Nice airport runs and premium hospitality.",
    canonical: "/en/chauffeur/iltm-cannes",
  },
};

export default iltmCannes;
